import CustomAttributeField from "#common/CustomAttributeField";
import { displayValueByDataType } from "#src/utils/display";
import {
  // RadioInput,
  type FilterBuilderFieldsType,
  type HeaderType,
  type ItemBaseType,
} from "@validereinc/common-components";
import { AttributeDataType, type AssetTypeType } from "@validereinc/domain";
import React, { useMemo } from "react";
import { useListCustomAttributes } from "../adapters/useCustomAttributes";
import { getCustomAttributeDisplayOpts } from "./useCustomAttributeFilters.helpers";

/** @deprecated will be removed in the near future */
export const useCustomAttributeFilters = <ItemType extends ItemBaseType>({
  assetType,
  section = "Filters",
  prefix = "custom_attributes",
  enabled = true,
  sortable = false,
  groupable = false,
  headerRenderOpts,
}: {
  assetType: AssetTypeType;
  section?: string;
  prefix?: string;
  enabled?: boolean;
  sortable?: boolean;
  groupable?: boolean;
  headerRenderOpts?: Parameters<typeof displayValueByDataType>[2];
}) => {
  const { data, isLoading } = useListCustomAttributes(
    { filters: { entity_type: assetType } },
    { enabled }
  );
  const customAttributes = data?.data ?? [];
  const customAttributeHeaders: Array<HeaderType<ItemType>> = useMemo(() => {
    return customAttributes.map(
      ({
        display_name,
        field_name,
        data_type,
        entity_subtypes,
        lookup_entity_type,
      }) => ({
        label: display_name,
        key: `${prefix}.${field_name}`,
        isSelected: !entity_subtypes?.length,
        isSortable: sortable,
        isGroupable: groupable,
        field_name,
        renderComponent: ({ value, opts }) =>
          displayValueByDataType(
            value,
            data_type,
            getCustomAttributeDisplayOpts({
              dataType: data_type,
              lookupEntityType: lookup_entity_type,
              headerRenderOpts,
              opts,
            })
          ),
      })
    );
  }, [customAttributes, headerRenderOpts, sortable, groupable]);

  const customAttributeFilters = useMemo(() => {
    return customAttributes.map((attribute) => {
      const name = prefix
        ? `${prefix}.${attribute.field_name}`
        : attribute.field_name;

      // file custom attributes can only be searched for existence
      if (
        attribute.data_type === "file" ||
        attribute.data_type === "multi-file"
      ) {
        // file attributes are not filterable for the moment
        return {
          section,
          component: <></>,
        };
        // TODO: will be wired up in CHB-7447
        // return {
        //   section,
        //   component: (
        //     <RadioInput
        //       name={name}
        //       label={attribute.display_name}
        //       options={[
        //         { label: "File exists", value: true },
        //         { label: "File does not exist", value: false },
        //       ]}
        //       labelKey="label"
        //       valueKey="value"
        //       isRequired={false}
        //       isDisabled={false}
        //       isOptionalTextShown={false}
        //     />
        //   ),
        // };
      }

      return {
        section,
        component: (
          <CustomAttributeField
            key={name}
            name={name}
            attribute={{ ...attribute, is_required: false }}
            isDisabled={false}
            isOptionalTextShown={false}
            isFilterInput
          />
        ),
      };
    });
  }, [customAttributes, prefix]);

  return {
    customAttributes,
    customAttributeHeaders,
    customAttributeFilters,
    isLoading: enabled && isLoading,
  };
};

export const useCustomAttributeFiltersV2 = <ItemType extends ItemBaseType>({
  assetType,
  prefix = "custom_attributes",
  enabled = true,
  sortable = false,
  groupable = false,
  headerRenderOpts,
}: {
  assetType: AssetTypeType;
  prefix?: string;
  enabled?: boolean;
  sortable?: boolean;
  groupable?: boolean;
  headerRenderOpts?: Parameters<typeof displayValueByDataType>[2];
}) => {
  const { data, isLoading } = useListCustomAttributes(
    { filters: { entity_type: assetType } },
    { enabled }
  );
  const customAttributes = data?.data ?? [];

  const customAttributeHeaders: Array<HeaderType<ItemType>> = useMemo(() => {
    return customAttributes.map(
      ({
        display_name,
        field_name,
        data_type,
        entity_subtypes,
        lookup_entity_type,
      }) => ({
        label: display_name,
        key: `${prefix}.${field_name}`,
        isSelected: !entity_subtypes?.length,
        isSortable: sortable,
        isGroupable: groupable,
        renderComponent: ({ value, opts }) =>
          displayValueByDataType(
            value,
            data_type,
            getCustomAttributeDisplayOpts({
              dataType: data_type,
              lookupEntityType: lookup_entity_type,
              headerRenderOpts,
              opts,
            })
          ),
      })
    );
  }, [customAttributes, headerRenderOpts, sortable, groupable]);

  const customAttributeFilterBuilderFieldDefinitions = useMemo(() => {
    return customAttributes.reduce<FilterBuilderFieldsType>(
      (
        filterBuilderFieldDefinitions,
        { display_name, field_name, data_type, pick_list_values }
      ) => {
        switch (data_type) {
          case AttributeDataType.PICK_LIST:
            filterBuilderFieldDefinitions[`custom_attributes.${field_name}`] = {
              data_type,
              display_name,
              pick_list_options: pick_list_values ?? [],
            };
            break;
          case AttributeDataType.DATE:
            filterBuilderFieldDefinitions[`custom_attributes.${field_name}`] = {
              data_type,
              display_name,
              format: "yyyyMM",
            };
            break;
          case AttributeDataType.STRING:
          case AttributeDataType.NUMBER:
          case AttributeDataType.BOOLEAN:
            filterBuilderFieldDefinitions[`custom_attributes.${field_name}`] = {
              data_type,
              display_name,
            };
            break;
          default:
            break;
        }
        return filterBuilderFieldDefinitions;
      },
      {} as FilterBuilderFieldsType
    );
  }, [customAttributes]);

  const customAttributeFilters = useMemo(() => {
    return customAttributes.map((attribute) => {
      const name = prefix
        ? `${prefix}.${attribute.field_name}`
        : attribute.field_name;

      return (
        <CustomAttributeField
          key={name}
          name={name}
          attribute={{ ...attribute, is_required: false }}
          isDisabled={false}
          isOptionalTextShown={false}
          isFilterInput
        />
      );
    });
  }, [customAttributes, prefix]);

  return {
    customAttributes,
    customAttributeHeaders,
    customAttributeFilters,
    customAttributeFilterBuilderFieldDefinitions,
    isLoading: enabled && isLoading,
  };
};
